import { Controller, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { zodResolver } from '@hookform/resolvers/zod'

import { UserPlusIcon } from '@gto/icons-react/outline/user-plus'
import { Button } from '@gto/ui/button'
import { Typography } from '@gto/ui/typography'

import type { AffiliateAdminAccessGrantSchema } from '@/api/gen'
import { fieldErrorText, TextField } from '@/components/form'
import {
  ADMIN_ACCESS_ERROR_PREFIX,
  ADMIN_ACCESS_GRANT_DEFAULTS,
  adminAccessGrantFormSchema,
  type AdminAccessGrantFormValues,
  buildAdminAccessGrantRequest,
} from '@/domain/admin-access'

type GrantAccessFormProps = {
  submitting: boolean
  onSubmit: (body: AffiliateAdminAccessGrantSchema, onGranted: () => void) => void
}

export const GrantAccessForm = ({ submitting, onSubmit }: GrantAccessFormProps) => {
  const { formatMessage } = useIntl()
  const form = useForm<AdminAccessGrantFormValues>({
    resolver: zodResolver(adminAccessGrantFormSchema),
    defaultValues: ADMIN_ACCESS_GRANT_DEFAULTS,
  })

  return (
    <form
      noValidate
      className="flex flex-col gap-3"
      data-tst="grant-access-form"
      onSubmit={(event) =>
        void form.handleSubmit((values) =>
          onSubmit(buildAdminAccessGrantRequest(values), () =>
            form.reset(ADMIN_ACCESS_GRANT_DEFAULTS),
          ),
        )(event)
      }
    >
      <Typography
        color="text.secondary"
        variant="body2"
      >
        <FormattedMessage id="adminAccess.grant.description" />
      </Typography>
      <Controller
        control={form.control}
        name="email"
        render={({ field, fieldState }) => (
          <TextField
            ref={field.ref}
            required
            autoComplete="off"
            className="max-w-96"
            data-tst="grant-access-email"
            disabled={submitting}
            error={fieldErrorText(formatMessage, fieldState.error, ADMIN_ACCESS_ERROR_PREFIX)}
            label={formatMessage({ id: 'adminAccess.grant.email' })}
            name={field.name}
            type="email"
            value={field.value}
            onBlur={field.onBlur}
            onChange={(e) => field.onChange(e.target.value)}
          />
        )}
      />
      <Button
        className="self-start"
        disabled={submitting}
        type="submit"
        variant="contained"
        startIcon={
          <UserPlusIcon
            height={18}
            width={18}
          />
        }
      >
        <FormattedMessage id="adminAccess.grant.submit" />
      </Button>
    </form>
  )
}
